import { Fragment, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusChip } from "@/components/shared/StatusChip";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import { WorkScopeLoadingScreen } from "@/components/shared/WorkScopeLoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest } from "@/lib/api";
import { fetchCompanySettings } from "@/lib/company";
import { fetchClientById, type ClientItem } from "@/lib/projects-clients";
import { invalidateTicketData } from "@/lib/query-invalidation";
import { formatCurrency, mapApiStatusToUi, mapApiTypeToLabel } from "@/lib/tickets";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";
import { deleteAttachment, uploadTicketAttachment } from "@/lib/attachments";
import { ArrowLeft, Bell, Download, Eye, Loader2, Send } from "lucide-react";
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
const SMS_APPROVALS_ENABLED =
  ((import.meta.env.VITE_SMS_APPROVALS_ENABLED as string | undefined) ?? "false") === "true";

type TicketDetailResponse = {
  id: string;
  ticketNumber: string;
  type: "CHANGE_ORDER" | "TM_TICKET" | "EXTRA_WORK";
  status: "DRAFT" | "PENDING_APPROVAL" | "APPROVED" | "REJECTED" | "NEEDS_CLARIFICATION" | "ARCHIVED";
  title: string;
  description?: string;
  createdAt: string;
  approvedAt?: string | null;
  sentAt?: string | null;
  totalAmount: string;
  subtotalAmount: string;
  taxAmount: string;
  internalNotes?: string | null;
  project: { id: string; name: string };
  client?: { id: string; name: string; email?: string | null; phone?: string | null; address?: string | null } | null;
  createdBy: { id: string; fullName: string; email: string };
  lineItems: Array<{ id: string; category: string; description: string; quantity: string; unit: string; unitCost: string; totalCost: string }>;
  attachments: Array<{
    id: string;
    fileName: string;
    mimeType: string;
    storagePath: string;
    fileSize: number;
    tag: string;
    createdAt: string;
    url?: string;
  }>;
  approvalRequests: Array<{
    id: string;
    recipientName: string;
    recipientEmail: string;
    recipientPhone?: string | null;
    deliveryMethod?: "EMAIL" | "SMS" | "EMAIL_AND_SMS";
    sentAt?: string | null;
    publicToken?: string;
    smsMessages?: Array<{
      id: string;
      messageKind: "INITIAL_SEND" | "REMINDER" | "RESEND";
      recipientPhone: string;
      latestStatus: "ACCEPTED" | "QUEUED" | "SENT" | "DELIVERED" | "FAILED" | "UNDELIVERED";
      latestTwilioStatus?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      lastStatusAt?: string | null;
      createdAt: string;
      twilioMessageSid?: string | null;
    }>;
  }>;
  approvalEvents: Array<{
    id: string;
    eventType: string;
    actorName?: string | null;
    actorEmail?: string | null;
    happenedAt: string;
    payload?: Record<string, unknown> | null;
  }>;
};

type PricingDisplayMode =
  | "final_price_only"
  | "summary_breakdown"
  | "full_itemized_breakdown";

type PricingMetadata = {
  pricingVersion?: number;
  pricingMode?: string;
  customerDisplayMode?: PricingDisplayMode;
  customerPrice?: number;
  discountAmount?: number;
  taxAmount?: number;
  finalAmount?: number;
  changeOrder?: {
    reasonForChange?: string;
    reasonForChangeLabel?: string;
    changeType?: string;
    changeTypeLabel?: string;
  };
  extraWork?: {
    reason?: string;
    reasonLabel?: string;
  };
  internalEstimate?: {
    rows?: Array<{
      id?: string;
      category?: string;
      description?: string;
      quantity?: number;
      unit?: string;
      unitCost?: number;
      lineTotal?: number;
      thumbnailPath?: string | null;
      thumbnailUrl?: string | null;
      internalNote?: string;
    }>;
    internalSubtotal?: number;
    customerFacingFinalPrice?: number;
  } | null;
};

type ClarificationThreadItem = {
  id: string;
  eventType: string;
  authorType: "CUSTOMER" | "INTERNAL" | "SYSTEM";
  authorLabel: string;
  happenedAt: string;
  message: string;
};

const withCacheBust = (url: string) => {
  const [base, hash = ""] = url.split("#");
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}v=${Date.now()}${hash ? `#${hash}` : ""}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

function formatCompactNumber(value: number) {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

function parsePricingMetadata(value?: string | null): PricingMetadata | null {
  if (!value?.trim()) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as PricingMetadata;
    return typeof record.pricingVersion === "number" ? record : null;
  } catch {
    return null;
  }
}

function formatPricingCategoryLabel(value?: string | null) {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized.includes("labor")) return "Labor";
  if (normalized.includes("material")) return "Materials";
  if (normalized.includes("equipment")) return "Equipment";
  if (normalized.includes("subcontract")) return "Subcontractor";
  return "Other";
}

function formatPricingUnitLabel(value?: string | null) {
  const normalized = String(value ?? "").toLowerCase();
  if (["hour", "hours", "hr", "hrs"].includes(normalized)) return "hrs";
  if (["each", "ea", "unit", "units"].includes(normalized)) return "ea";
  if (["sq_ft", "sqft", "sf", "sq ft"].includes(normalized)) return "sq ft";
  if (["cu_yd", "cu yd", "cuyd"].includes(normalized)) return "cu yd";
  if (normalized === "fixed") return "fixed";
  return value ?? "";
}

function formatCustomerDisplayMode(value?: string | null) {
  if (value === "summary_breakdown") return "Summary breakdown";
  if (value === "full_itemized_breakdown") return "Full itemized breakdown";
  return "Final price only";
}

function formatChangeOrderReason(value?: string | null) {
  if (value === "client_request") return "Client request";
  if (value === "unforeseen_condition") return "Unforeseen condition";
  if (value === "material_change") return "Material change";
  if (value === "scope_change") return "Scope change";
  if (value === "code_permit_requirement") return "Code / permit requirement";
  if (value === "other") return "Other";
  return value ?? "";
}

function formatChangeOrderType(value?: string | null) {
  if (value === "add_cost") return "Add cost";
  if (value === "credit_deduct") return "Credit / deduct";
  if (value === "no_cost_change") return "No cost change";
  return value ?? "";
}

function formatExtraWorkReason(value?: string | null) {
  if (value === "client_request") return "Client request";
  if (value === "additional_scope") return "Additional scope";
  if (value === "emergency_urgent_work") return "Emergency / urgent work";
  if (value === "site_condition") return "Site condition";
  if (value === "after_hours_work") return "After-hours work";
  if (value === "material_service_add_on") return "Material/service add-on";
  if (value === "other") return "Other";
  return value ?? "";
}

function getTicketDetailQtyUnit(item: TicketDetailResponse["lineItems"][number]) {
  const quantity = Number(item.quantity ?? 0);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return { qtyText: "", unitText: "" };
  }

  const qtyText = formatCompactNumber(quantity);
  const category = String(item.category ?? "").trim().toUpperCase();
  const rawUnit = String(item.unit ?? "").trim();
  const normalizedUnit = rawUnit.toLowerCase();
  const isLabor = category === "LABOR" || category === "WORK";

  if (isLabor) {
    if (["fixed", "fixed price", "lump sum", "lump-sum", "ls"].includes(normalizedUnit)) {
      return { qtyText: "", unitText: "" };
    }
    if (["hrs", "hr", "hour", "hours", "h", "", "qty", "quantity"].includes(normalizedUnit)) {
      return { qtyText, unitText: "h" };
    }
    if (quantity === 1 && ["ea", "each", "unit"].includes(normalizedUnit)) {
      return { qtyText: "", unitText: "" };
    }
  }

  if (!rawUnit || ["qty", "quantity"].includes(normalizedUnit)) {
    return { qtyText, unitText: "" };
  }

  if (quantity === 1 && ["ea", "each", "unit"].includes(normalizedUnit)) {
    return { qtyText, unitText: "" };
  }

  return { qtyText, unitText: rawUnit };
}

const isImageAttachment = (mimeType?: string, fileName?: string) => {
  const normalizedMime = (mimeType ?? "").toLowerCase();
  if (normalizedMime.startsWith("image/")) return true;
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(fileName ?? "");
};

const normalizePhoneForDelivery = (value?: string | null) => {
  const raw = value?.trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    const normalized = `+${raw.slice(1).replace(/\D/g, "")}`;
    return /^\+\d{10,15}$/.test(normalized) ? normalized : "";
  }
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
};

export default function TicketDetail() {
  const { t } = useTranslation();
  const lineItemCategoryText = (category?: string | null) => {
    const normalized = category?.trim().toUpperCase() ?? "";
    if (normalized === "LABOR" || normalized === "WORK") return t("ticketDetail.categories.labor");
    if (normalized === "MATERIAL" || normalized === "MATERIALS") return t("ticketDetail.categories.materials");
    if (normalized === "EQUIPMENT") return t("ticketDetail.categories.equipment");
    return t("ticketDetail.categories.other");
  };
  const approvalEventText = (eventType: string) => {
    switch (eventType) {
      case "APPROVAL_EMAIL_SENT":
        return t("ticketDetail.events.approvalEmailSent");
      case "APPROVAL_SMS_SENT":
        return t("ticketDetail.events.approvalSmsSent");
      case "SMS_STATUS_ACCEPTED":
        return t("ticketDetail.events.smsAccepted");
      case "SMS_STATUS_QUEUED":
        return t("ticketDetail.events.smsQueued");
      case "SMS_STATUS_SENT":
        return t("ticketDetail.events.smsSent");
      case "SMS_STATUS_DELIVERED":
        return t("ticketDetail.events.smsDelivered");
      case "SMS_STATUS_FAILED":
        return t("ticketDetail.events.smsFailed");
      case "SMS_STATUS_UNDELIVERED":
        return t("ticketDetail.events.smsUndelivered");
      case "APPROVAL_LINK_OPENED":
        return t("ticketDetail.events.opened");
      case "APPROVAL_LINK_RESENT":
        return t("ticketDetail.events.approvalLinkResent");
      case "APPROVAL_LINK_SMS_RESENT":
        return t("ticketDetail.events.approvalLinkSmsResent");
      case "REMINDER_SENT":
        return t("ticketDetail.events.reminderSent");
      case "REMINDER_SMS_SENT":
        return t("ticketDetail.events.reminderSmsSent");
      case "APPROVED":
        return t("ticketDetail.events.approved");
      case "APPROVED_ONSITE":
        return t("ticketDetail.events.approvedOnSite");
      case "REJECTED":
        return t("ticketDetail.events.rejected");
      case "CLARIFICATION_REQUESTED":
        return t("ticketDetail.events.clarificationRequested");
      case "CLARIFICATION_RESPONSE_SENT":
        return t("ticketDetail.events.clarificationResponseSent");
      case "COMMENT_ADDED":
        return t("ticketDetail.events.commentAdded");
      case "TICKET_CREATED":
        return t("ticketDetail.events.ticketCreated");
      case "DRAFT_CREATED":
        return t("ticketDetail.events.draftCreated");
      case "DELETED":
        return t("ticketDetail.events.deleted");
      case "SENT_FOR_APPROVAL":
        return t("ticketDetail.events.sentForApproval");
      case "MANUAL_APPROVED":
        return t("ticketDetail.events.approvedByAdmin");
      case "MANUAL_REJECTED":
        return t("ticketDetail.events.rejectedByAdmin");
      case "MANUAL_APPROVAL_OVERRIDE":
        return t("ticketDetail.events.manualAdminOverride");
      default:
        return eventType.replaceAll("_", " ").toLowerCase().replace(/^\w/, (char) => char.toUpperCase());
    }
  };
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<TicketDetailResponse | null>(null);
  const [clientDetail, setClientDetail] = useState<ClientItem | null>(null);

  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"EMAIL" | "SMS" | "EMAIL_AND_SMS">("EMAIL");
  const [workspaceAutoRemindersEnabled, setWorkspaceAutoRemindersEnabled] = useState(true);
  const [allowManualOverride, setAllowManualOverride] = useState(false);
  const [requireRejectionComment, setRequireRejectionComment] = useState(true);
  const [reminderCadenceOption, setReminderCadenceOption] = useState("3");
  const [customReminderCadenceDays, setCustomReminderCadenceDays] = useState("");
  const [recipientPhoneError, setRecipientPhoneError] = useState("");
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState("");
  const [smsActionConsentConfirmed, setSmsActionConsentConfirmed] = useState(false);
  const [smsActionConsentError, setSmsActionConsentError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingReminderNow, setSendingReminderNow] = useState(false);
  const [downloadingTicketPdf, setDownloadingTicketPdf] = useState(false);
  const [sendingTicketPdfEmail, setSendingTicketPdfEmail] = useState(false);
  const [ticketPdfUrl, setTicketPdfUrl] = useState<string | null>(null);
  const [loadingTicketPdfMeta, setLoadingTicketPdfMeta] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [showTechnicalEvents, setShowTechnicalEvents] = useState(false);
  const [clarificationMessage, setClarificationMessage] = useState("");
  const [respondingClarification, setRespondingClarification] = useState(false);
  const [manualDecisionOpen, setManualDecisionOpen] = useState(false);
  const [manualDecisionType, setManualDecisionType] = useState<"APPROVED" | "REJECTED">("APPROVED");
  const [manualDecisionReason, setManualDecisionReason] = useState("");
  const [processingManualDecision, setProcessingManualDecision] = useState(false);

  const goToTicketsList = () => {
    navigate({
      pathname: "/tickets",
      search: location.search || "",
    });
  };

  const loadTicket = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<TicketDetailResponse>(`/tickets/${id}`, { method: "GET" });
      setTicket(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTicket();
  }, [id]);

  useEffect(() => {
    const loadClientDetail = async () => {
      if (!ticket?.client?.id) {
        setClientDetail(null);
        return;
      }

      try {
        const data = await fetchClientById(ticket.client.id);
        setClientDetail(data);
      } catch {
        setClientDetail(null);
      }
    };

    void loadClientDetail();
  }, [ticket?.client?.id]);

  const normalizedClientPhone = useMemo(() => {
    return normalizePhoneForDelivery(ticket?.client?.phone);
  }, [ticket?.client?.phone]);

  useEffect(() => {
    const latestApproval = ticket?.approvalRequests?.[0];
    setRecipientName(latestApproval?.recipientName ?? ticket?.client?.name ?? "");
    setRecipientEmail(latestApproval?.recipientEmail ?? ticket?.client?.email ?? "");
    setRecipientPhone(latestApproval?.recipientPhone ?? normalizedClientPhone);
    setDeliveryMethod(latestApproval?.deliveryMethod ?? "EMAIL");
    setRecipientPhoneError("");
  }, [normalizedClientPhone, ticket?.approvalRequests, ticket?.client?.email, ticket?.client?.name]);

  useEffect(() => {
    const loadReminderSettings = async () => {
      try {
        const settings = await fetchCompanySettings();
        setWorkspaceAutoRemindersEnabled(settings.reminderDayOffsets.length > 0);
        setAllowManualOverride(settings.allowManualApprovalOverride);
        setRequireRejectionComment(settings.requireRejectionComment);
      } catch {
        setWorkspaceAutoRemindersEnabled(true);
        setAllowManualOverride(false);
      }
    };

    void loadReminderSettings();
  }, []);

  const latestApprovalRequest = ticket?.approvalRequests?.[0] ?? null;
  const canSendTicketPdfEmail = Boolean(ticket?.client?.email?.trim());
  const canSend = useMemo(() => {
    if (!recipientName.trim()) return false;
    const requiresEmail = deliveryMethod === "EMAIL" || deliveryMethod === "EMAIL_AND_SMS";
    const requiresPhone = deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";
    const hasEmail = !requiresEmail || recipientEmail.includes("@");
    const hasPhone = !requiresPhone || Boolean(normalizePhoneForDelivery(recipientPhone));
    const hasSmsConsent = !requiresPhone || smsConsentConfirmed;
    return hasEmail && hasPhone && hasSmsConsent;
  }, [deliveryMethod, recipientEmail, recipientName, recipientPhone, smsConsentConfirmed]);
  const canRespondClarification = useMemo(
    () => (user?.role === "ADMIN" || user?.role === "PM") && clarificationMessage.trim().length > 0,
    [clarificationMessage, user?.role],
  );
  const manualFinalizationEvent = useMemo(
    () =>
      ticket?.approvalEvents.find((event) =>
        ["MANUAL_APPROVED", "MANUAL_REJECTED", "MANUAL_APPROVAL_OVERRIDE"].includes(event.eventType),
      ) ?? null,
    [ticket],
  );
  const canUseManualOverride = useMemo(
    () =>
      user?.role === "ADMIN" &&
      allowManualOverride &&
      !!ticket &&
      ["PENDING_APPROVAL", "NEEDS_CLARIFICATION"].includes(ticket.status),
    [allowManualOverride, ticket, user?.role],
  );
  const resolvedClientEmail = clientDetail?.email ?? ticket?.client?.email ?? "-";
  const resolvedClientAddress = clientDetail?.address ?? ticket?.client?.address ?? "-";
  const latestApprovalSmsMessages = latestApprovalRequest?.smsMessages ?? [];
  const latestSmsMessage = latestApprovalSmsMessages[0] ?? null;
  const latestSmsDeliveryFailed =
    latestSmsMessage?.latestStatus === "FAILED" || latestSmsMessage?.latestStatus === "UNDELIVERED";
  const latestApprovalUsesEmail =
    latestApprovalRequest?.deliveryMethod === "EMAIL" ||
    latestApprovalRequest?.deliveryMethod === "EMAIL_AND_SMS";
  const fallbackRecipientEmail = (
    latestApprovalRequest?.recipientEmail ??
    clientDetail?.email ??
    ticket?.client?.email ??
    ""
  ).trim();
  const canSendEmailFallback =
    Boolean(ticket?.id) &&
    Boolean(latestApprovalRequest) &&
    fallbackRecipientEmail.includes("@");
  const smsStatusLabel = (status: string) => {
    switch (status) {
      case "ACCEPTED":
        return t("ticketDetail.smsStatuses.accepted");
      case "QUEUED":
        return t("ticketDetail.smsStatuses.queued");
      case "SENT":
        return t("ticketDetail.smsStatuses.sent");
      case "DELIVERED":
        return t("ticketDetail.smsStatuses.delivered");
      case "FAILED":
        return t("ticketDetail.smsStatuses.failed");
      case "UNDELIVERED":
        return t("ticketDetail.smsStatuses.undelivered");
      default:
        return status;
    }
  };
  const smsStatusClassName = (status: string) => {
    switch (status) {
      case "DELIVERED":
        return "border-emerald-200 bg-emerald-50 text-emerald-700";
      case "FAILED":
      case "UNDELIVERED":
        return "border-destructive/20 bg-destructive/10 text-destructive";
      case "SENT":
        return "border-sky-200 bg-sky-50 text-sky-700";
      case "QUEUED":
      case "ACCEPTED":
      default:
        return "border-amber-200 bg-amber-50 text-amber-700";
    }
  };
  const smsMessageKindLabel = (kind: string) => {
    switch (kind) {
      case "INITIAL_SEND":
        return t("ticketDetail.smsKinds.initialSend");
      case "REMINDER":
        return t("ticketDetail.smsKinds.reminder");
      case "RESEND":
        return t("ticketDetail.smsKinds.resend");
      default:
        return kind;
    }
  };
  const timelineEvents = useMemo(() => {
    if (!ticket) return [];
    const meaningfulEvents = new Set([
      "APPROVAL_EMAIL_SENT",
      "APPROVAL_SMS_SENT",
      "SMS_STATUS_ACCEPTED",
      "SMS_STATUS_QUEUED",
      "SMS_STATUS_SENT",
      "SMS_STATUS_DELIVERED",
      "SMS_STATUS_FAILED",
      "SMS_STATUS_UNDELIVERED",
      "APPROVAL_LINK_OPENED",
      "APPROVAL_LINK_RESENT",
      "APPROVAL_LINK_SMS_RESENT",
      "REMINDER_SENT",
      "REMINDER_SMS_SENT",
      "APPROVED",
      "REJECTED",
      "CLARIFICATION_REQUESTED",
      "CLARIFICATION_RESPONSE_SENT",
      "COMMENT_ADDED",
      "APPROVED_ONSITE",
      "MANUAL_APPROVED",
      "MANUAL_REJECTED",
      "MANUAL_APPROVAL_OVERRIDE",
    ]);
    if (showTechnicalEvents) return ticket.approvalEvents;
    return ticket.approvalEvents.filter((event) => meaningfulEvents.has(event.eventType));
  }, [showTechnicalEvents, ticket]);
  const clarificationThread = useMemo<ClarificationThreadItem[]>(() => {
    if (!ticket) return [];
    const recipientEmail = ticket.approvalRequests[0]?.recipientEmail?.toLowerCase() ?? "";
    return ticket.approvalEvents
      .filter((event) =>
        ["CLARIFICATION_REQUESTED", "CLARIFICATION_RESPONSE_SENT", "COMMENT_ADDED"].includes(event.eventType),
      )
      .map((event) => {
        const payload = event.payload ?? {};
        const messageRaw =
          typeof payload.message === "string"
            ? payload.message
            : typeof payload.comment === "string"
              ? payload.comment
              : event.eventType === "CLARIFICATION_REQUESTED"
                ? t("ticketDetail.clarificationRequestedSummary")
                : "";
        const actorEmail = event.actorEmail?.toLowerCase() ?? "";
        const isCustomerEvent =
          event.eventType === "CLARIFICATION_REQUESTED" ||
          (event.eventType === "COMMENT_ADDED" && !!recipientEmail && actorEmail === recipientEmail);
        const authorType: ClarificationThreadItem["authorType"] =
          event.eventType === "CLARIFICATION_RESPONSE_SENT"
            ? "INTERNAL"
            : isCustomerEvent
              ? "CUSTOMER"
              : "SYSTEM";
        const authorLabel =
          authorType === "CUSTOMER"
            ? t("ticketDetail.customer")
            : authorType === "INTERNAL"
              ? t("ticketDetail.workscopeTeam")
              : t("ticketDetail.system");
        return {
          id: event.id,
          eventType: event.eventType,
          authorType,
          authorLabel,
          happenedAt: event.happenedAt,
          message: messageRaw.trim(),
        };
      })
      .filter((item) => item.message.length > 0)
      .sort((a, b) => new Date(a.happenedAt).getTime() - new Date(b.happenedAt).getTime());
  }, [ticket, t]);
  const clarificationMode = useMemo(
    () => ticket?.status === "NEEDS_CLARIFICATION" || clarificationThread.length > 0,
    [ticket?.status, clarificationThread.length],
  );
  const isApprovedTicket = ticket?.status === "APPROVED";
  const isPendingApprovalTicket = ticket?.status === "PENDING_APPROVAL";
  const showSendForApprovalSection = useMemo(
    () => !!ticket && !clarificationMode && ticket.status !== "APPROVED",
    [clarificationMode, ticket],
  );
  const showTicketPdfSection = ticket?.status === "APPROVED";
  const pricingMetadata = useMemo(
    () => parsePricingMetadata(ticket?.internalNotes),
    [ticket?.internalNotes],
  );
  const internalEstimateRows = useMemo(
    () =>
      (pricingMetadata?.internalEstimate?.rows ?? []).filter((row) =>
        row.description?.trim(),
      ),
    [pricingMetadata],
  );
  const internalEstimateSummary = useMemo(() => {
    const categoryOrder = ["Labor", "Materials", "Equipment", "Subcontractor", "Other"];
    const totals = new Map<string, number>();
    let total = 0;

    internalEstimateRows.forEach((row) => {
      const rowTotal = Number(row.lineTotal ?? 0);
      if (!Number.isFinite(rowTotal)) return;
      const categoryLabel = formatPricingCategoryLabel(row.category);
      totals.set(categoryLabel, (totals.get(categoryLabel) ?? 0) + rowTotal);
      total += rowTotal;
    });

    const categoryTotals = categoryOrder
      .map((label) => ({ label, amount: totals.get(label) ?? 0 }))
      .filter(({ amount }) => Math.abs(amount) > 0.005);

    return { categoryTotals, total };
  }, [internalEstimateRows]);
  const hasInternalEstimate = internalEstimateRows.length > 0;
  const hasInternalEstimateImages = internalEstimateRows.some((row) =>
    Boolean(row.thumbnailUrl || row.thumbnailPath),
  );
  const hasNewPricingMetadata = Boolean(pricingMetadata?.pricingVersion);
  const changeOrderDetails = useMemo(() => {
    if (ticket?.type !== "CHANGE_ORDER") return null;
    const metadata = pricingMetadata?.changeOrder;
    const reason = metadata?.reasonForChangeLabel || formatChangeOrderReason(metadata?.reasonForChange);
    const changeType = metadata?.changeTypeLabel || formatChangeOrderType(metadata?.changeType);
    if (!reason && !changeType) return null;
    return { reason, changeType };
  }, [pricingMetadata?.changeOrder, ticket?.type]);
  const extraWorkDetails = useMemo(() => {
    if (ticket?.type !== "EXTRA_WORK") return null;
    const metadata = pricingMetadata?.extraWork;
    const reason = metadata?.reasonLabel || formatExtraWorkReason(metadata?.reason);
    if (!reason) return null;
    return { reason };
  }, [pricingMetadata?.extraWork, ticket?.type]);
  const hasCostBreakdown = useMemo(() => {
    if (!ticket) return false;
    if (hasNewPricingMetadata) return false;
    const subtotal = Number(ticket.subtotalAmount || 0);
    const total = Number(ticket.totalAmount || 0);
    if (subtotal > 0 || total > 0) return true;
    return ticket.lineItems.some((item) => Number(item.totalCost || 0) > 0);
  }, [hasNewPricingMetadata, ticket]);
  const costBreakdownRows = useMemo(
    () =>
      (ticket?.lineItems ?? []).map((item) => ({
        item,
        ...getTicketDetailQtyUnit(item),
      })),
    [ticket?.lineItems],
  );
  const hasDisplayUnitColumn = useMemo(
    () => costBreakdownRows.some((row) => row.unitText.length > 0),
    [costBreakdownRows],
  );
  const latestCustomerClarification = useMemo(
    () =>
      [...clarificationThread]
        .reverse()
        .find((item) => item.authorType === "CUSTOMER"),
    [clarificationThread],
  );
  const clarificationThreadAfterSummary = useMemo(() => {
    if (!latestCustomerClarification) {
      return clarificationThread.filter(
        (item) => !(item.eventType === "CLARIFICATION_REQUESTED" && item.message.toLowerCase() === "clarification requested."),
      );
    }

    let removedSummaryMessage = false;
    return clarificationThread.filter((item) => {
      if (item.eventType === "CLARIFICATION_REQUESTED" && item.message.toLowerCase() === "clarification requested.") {
        return false;
      }
      if (!removedSummaryMessage && item.id === latestCustomerClarification.id) {
        removedSummaryMessage = true;
        return false;
      }
      return true;
    });
  }, [clarificationThread, latestCustomerClarification]);
  const lastReminderAt = useMemo(() => {
    if (!ticket) return null;
    const reminderEvents = ticket.approvalEvents
      .filter((event) => event.eventType === "REMINDER_SENT")
      .sort((a, b) => new Date(b.happenedAt).getTime() - new Date(a.happenedAt).getTime());
    return reminderEvents[0]?.happenedAt ?? null;
  }, [ticket]);
  const smsAllowedByPlan = user?.currentPlan !== "STARTER";
  const canUseSmsDelivery = SMS_APPROVALS_ENABLED && smsAllowedByPlan && Boolean(
    normalizedClientPhone ||
      normalizePhoneForDelivery(latestApprovalRequest?.recipientPhone),
  );
  const smsAvailabilityReason = !SMS_APPROVALS_ENABLED
    ? t("ticketDetail.smsUnavailableFlag")
    : !smsAllowedByPlan
      ? t("ticketDetail.smsUnavailablePlan")
    : !canUseSmsDelivery
      ? t("ticketDetail.smsUnavailableNoPhone")
      : "";
  const deliveryUsesSms = deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";
  const latestApprovalUsesSms =
    latestApprovalRequest?.deliveryMethod === "SMS" ||
    latestApprovalRequest?.deliveryMethod === "EMAIL_AND_SMS";

  useEffect(() => {
    if (!deliveryUsesSms) {
      setSmsConsentConfirmed(false);
      setSmsConsentError("");
    }
  }, [deliveryUsesSms]);

  useEffect(() => {
    if (!latestApprovalUsesSms) {
      setSmsActionConsentConfirmed(false);
      setSmsActionConsentError("");
    }
  }, [latestApprovalUsesSms]);

  const requireSmsActionConsent = () => {
    if (!latestApprovalUsesSms || smsActionConsentConfirmed) return true;
    setSmsActionConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
    toast({
      title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
      variant: "destructive",
    });
    return false;
  };

  useEffect(() => {
    if (!canUseSmsDelivery && deliveryMethod !== "EMAIL") {
      setDeliveryMethod("EMAIL");
    }
  }, [canUseSmsDelivery, deliveryMethod]);

  const sendApproval = async () => {
    const normalizedRecipientPhone = normalizePhoneForDelivery(recipientPhone);
    const requiresEmail = deliveryMethod === "EMAIL" || deliveryMethod === "EMAIL_AND_SMS";
    const requiresPhone = deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";
    const canSendByChannel =
      recipientName.trim() &&
      (!requiresEmail || recipientEmail.includes("@")) &&
      (!requiresPhone || Boolean(normalizedRecipientPhone));
    const missingSmsConsent = requiresPhone && !smsConsentConfirmed;
    setRecipientPhoneError(
      requiresPhone && !normalizedRecipientPhone
        ? t("ticketDetail.toasts.recipientPhoneRequired")
        : "",
    );
    setSmsConsentError(
      missingSmsConsent ? SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE : "",
    );
    if (!id || !canSendByChannel || missingSmsConsent) {
      if (missingSmsConsent) {
        toast({
          title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
          variant: "destructive",
        });
      }
      return;
    }

    let reminderCadenceDays: number | undefined;
    if (workspaceAutoRemindersEnabled) {
      if (reminderCadenceOption === "custom") {
        const parsed = Number(customReminderCadenceDays);
        if (!Number.isInteger(parsed) || parsed < 1) {
          toast({
        title: t("ticketDetail.toasts.invalidReminderCadenceTitle"),
        description: t("ticketDetail.toasts.invalidReminderCadenceDescription"),
            variant: "destructive",
          });
          return;
        }
        reminderCadenceDays = parsed;
      } else {
        reminderCadenceDays = Number(reminderCadenceOption);
      }
    }

    setSending(true);
    try {
      await apiRequest(`/tickets/${id}/send-approval`, {
        method: "POST",
        body: {
          deliveryMethod,
          recipientName,
          recipientEmail: requiresEmail ? recipientEmail : undefined,
          recipientPhone: requiresPhone ? normalizedRecipientPhone : undefined,
          smsConsentConfirmed: requiresPhone ? smsConsentConfirmed : undefined,
          ...(workspaceAutoRemindersEnabled ? { reminderCadenceDays } : {}),
        },
      });
      await invalidateTicketData(queryClient);
      window.dispatchEvent(new Event("workscope:notifications-refresh"));
      toast({ title: t("ticketDetail.toasts.sentForApprovalTitle"), description: t("ticketDetail.toasts.sentForApprovalDescription") });
      setRecipientName("");
      setRecipientEmail("");
      setRecipientPhone("");
      await loadTicket();
    } catch (e) {
      toast({ title: t("ticketDetail.toasts.sendFailedTitle"), description: e instanceof Error ? e.message : t("ticketDetail.toasts.sendFailedDescription"), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const sendEmailFallback = async () => {
    if (!ticket?.id || !latestApprovalRequest) return;

    const fallbackRecipientName =
      latestApprovalRequest.recipientName?.trim() || ticket.client?.name?.trim() || "";
    if (!fallbackRecipientName || !fallbackRecipientEmail.includes("@")) {
      toast({
        title: t("ticketDetail.toasts.emailFallbackUnavailableTitle"),
        description: t("ticketDetail.toasts.emailFallbackUnavailableDescription"),
        variant: "destructive",
      });
      return;
    }

    let reminderCadenceDays: number | undefined;
    if (workspaceAutoRemindersEnabled) {
      if (reminderCadenceOption === "custom") {
        const parsed = Number(customReminderCadenceDays);
        if (Number.isInteger(parsed) && parsed > 0) {
          reminderCadenceDays = parsed;
        }
      } else {
        reminderCadenceDays = Number(reminderCadenceOption);
      }
    }

    setSending(true);
    try {
      await apiRequest(`/tickets/${ticket.id}/send-approval`, {
        method: "POST",
        body: {
          deliveryMethod: "EMAIL",
          recipientName: fallbackRecipientName,
          recipientEmail: fallbackRecipientEmail,
          reminderCadenceDays,
        },
      });
      await invalidateTicketData(queryClient);
      window.dispatchEvent(new Event("workscope:notifications-refresh"));
      toast({ title: t("ticketDetail.toasts.emailFallbackSent") });
      await loadTicket();
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.emailFallbackFailedTitle"),
        description: e instanceof Error ? e.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };
  const smsFailureWarning = latestSmsDeliveryFailed ? (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="space-y-1">
        <p className="text-sm font-medium text-amber-900">
          {t("ticketDetail.smsDeliveryWarningTitle")}
        </p>
        <p className="text-sm text-amber-800">
          {latestApprovalUsesEmail
            ? t("ticketDetail.smsDeliveryWarningEmailAlsoSent")
            : t("ticketDetail.smsDeliveryWarningDescription")}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {canSendEmailFallback ? (
          <Button
            variant="outline"
            className="gap-1.5 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            disabled={sending}
            onClick={() => void sendEmailFallback()}
          >
            <Send className="h-4 w-4" />
            {sending ? t("login.sending") : t("ticketDetail.sendByEmailInstead")}
          </Button>
        ) : (
          <p className="text-xs text-amber-800">{t("ticketDetail.smsDeliveryWarningNoEmail")}</p>
        )}
        {latestApprovalRequest?.publicToken ? (
          <Button
            variant="ghost"
            className="gap-1.5 text-amber-900 hover:bg-amber-100"
            onClick={() => window.open(`/approve/${latestApprovalRequest.publicToken}`, "_blank", "noopener,noreferrer")}
          >
            <Eye className="h-4 w-4" />
            {t("ticketDetail.openApprovalPage")}
          </Button>
        ) : null}
      </div>
    </div>
  ) : null;

  const sendClarificationResponse = async () => {
    if (!id || !canRespondClarification) return;
    setRespondingClarification(true);
    try {
      await apiRequest(`/tickets/${id}/clarification-response`, {
        method: "POST",
        body: {
          message: clarificationMessage.trim(),
        },
      });
      await invalidateTicketData(queryClient);
      window.dispatchEvent(new Event("workscope:notifications-refresh"));
      toast({ title: t("ticketDetail.toasts.clarificationSentTitle") });
      setClarificationMessage("");
      await loadTicket();
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.clarificationFailedTitle"),
        description: e instanceof Error ? e.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setRespondingClarification(false);
    }
  };

  useEffect(() => {
    const loadTicketPdfMeta = async () => {
      if (!id || !showTicketPdfSection) {
        setTicketPdfUrl(null);
        return;
      }
      setLoadingTicketPdfMeta(true);
      try {
        const result = await apiRequest<{ url?: string | null }>(`/billing-exports/tickets/${id}/pdf`, {
          method: "GET",
        });
        setTicketPdfUrl(result?.url ? withCacheBust(result.url) : null);
      } catch {
        setTicketPdfUrl(null);
      } finally {
        setLoadingTicketPdfMeta(false);
      }
    };

    void loadTicketPdfMeta();
  }, [id, showTicketPdfSection]);

  const downloadTicketPdf = async () => {
    if (!id) return;
    setDownloadingTicketPdf(true);
    try {
      const response = await fetch(`${API_BASE}/tickets/${id}/pdf`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const disposition = response.headers.get("content-disposition") ?? "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] ?? `ticket-${id}.pdf`;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.pdfDownloadFailedTitle"),
        description: e instanceof Error ? e.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setDownloadingTicketPdf(false);
    }
  };

  const sendTicketPdfEmail = async () => {
    if (!id || !canSendTicketPdfEmail) return;
    setSendingTicketPdfEmail(true);
    try {
      const result = await apiRequest<{ recipientEmail?: string | null }>(
        `/billing-exports/tickets/${id}/send-pdf-email`,
        { method: "POST" },
      );
      toast({
        title: t("ticketDetail.toasts.pdfEmailSentTitle"),
        description: t("ticketDetail.toasts.pdfEmailSentDescription", {
          email: result?.recipientEmail ?? ticket?.client?.email ?? "",
        }),
      });
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.pdfEmailFailedTitle"),
        description: e instanceof Error ? e.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setSendingTicketPdfEmail(false);
    }
  };

  const openManualDecision = (decision: "APPROVED" | "REJECTED") => {
    setManualDecisionType(decision);
    setManualDecisionReason("");
    setManualDecisionOpen(true);
  };

  const submitManualDecision = async () => {
    if (!id || !ticket) return;

    if (
      manualDecisionType === "REJECTED" &&
      requireRejectionComment &&
      !manualDecisionReason.trim()
    ) {
      toast({
        title: t("ticketDetail.toasts.reasonRequiredTitle"),
        description: t("ticketDetail.toasts.reasonRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    setProcessingManualDecision(true);
    try {
      const path =
        manualDecisionType === "APPROVED"
          ? `/tickets/${id}/manual-approve`
          : `/tickets/${id}/manual-reject`;
      const nowIso = new Date().toISOString();

      await apiRequest(path, {
        method: "POST",
        body: {
          reason: manualDecisionReason.trim() || undefined,
        },
      });
      await invalidateTicketData(queryClient);

      setTicket((prev) =>
        prev
          ? {
              ...prev,
              status: manualDecisionType === "APPROVED" ? "APPROVED" : "REJECTED",
              approvalEvents: [
                ...prev.approvalEvents,
                {
                  id: `manual-${Date.now()}`,
                  eventType:
                    manualDecisionType === "APPROVED" ? "MANUAL_APPROVED" : "MANUAL_REJECTED",
                  actorName: user?.fullName ?? user?.email ?? "admin",
                  actorEmail: user?.email ?? "admin",
                  happenedAt: nowIso,
                  payload: manualDecisionReason.trim()
                    ? { reason: manualDecisionReason.trim() }
                    : undefined,
                },
              ],
            }
          : prev,
      );

      setManualDecisionOpen(false);
      setManualDecisionReason("");
      toast({
        title:
          manualDecisionType === "APPROVED"
            ? t("ticketDetail.toasts.ticketMarkedApproved")
            : t("ticketDetail.toasts.ticketMarkedRejected"),
      });
      window.dispatchEvent(new Event("workscope:notifications-refresh"));
      void loadTicket();
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.manualActionFailedTitle"),
        description: e instanceof Error ? e.message : t("ticketDetail.toasts.manualActionFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setProcessingManualDecision(false);
    }
  };

  const handleUploadAttachment = async (file: File | null) => {
    if (!id || !file) return;
    setUploadingAttachment(true);
    try {
      const uploaded = await uploadTicketAttachment({
        ticketId: id,
        file,
      });
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              attachments: [uploaded, ...prev.attachments],
            }
          : prev,
      );
      toast({ title: t("ticketDetail.toasts.attachmentUploaded") });
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.uploadFailedTitle"),
        description: e instanceof Error ? e.message : t("ticketDetail.toasts.uploadFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    setDeletingAttachmentId(attachmentId);
    try {
      await deleteAttachment(attachmentId);
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              attachments: prev.attachments.filter((file) => file.id !== attachmentId),
            }
          : prev,
      );
      toast({ title: t("ticketDetail.toasts.attachmentRemoved") });
    } catch (e) {
      toast({
        title: t("ticketDetail.toasts.removeFailedTitle"),
        description: e instanceof Error ? e.message : t("ticketDetail.toasts.removeFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const sendSectionDisabled = Boolean(manualFinalizationEvent);

  useEffect(() => {
    if (!isApprovedTicket) return;
    if (clarificationMessage.trim().length > 0) return;
    setClarificationMessage(t("ticketDetail.defaultApprovedResponse"));
  }, [isApprovedTicket, clarificationMessage]);

  if (loading) {
    return <WorkScopeLoadingScreen />;
  }

  if (error || !ticket) {
    return (
      <div className="space-y-3">
        <p className="text-destructive text-sm">{error ?? t("ticketDetail.notFound")}</p>
        <Button variant="outline" onClick={goToTicketsList}>{t("ticketDetail.backToTickets")}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <button onClick={goToTicketsList} className="p-2 rounded-md hover:bg-secondary">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-[#0C1F4F]">{ticket.ticketNumber}</h1>
            <StatusChip status={mapApiStatusToUi(ticket.status)} />
            <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{mapApiTypeToLabel(ticket.type)}</span>
          </div>
          <p className="text-sm text-muted-foreground">{ticket.title}</p>
        </div>
        {isPendingApprovalTicket ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              disabled={sendSectionDisabled}
              onClick={() => {
                const first = latestApprovalRequest;
                if (!first?.publicToken) {
                  toast({ title: t("ticketDetail.toasts.noApprovalLinkYet"), variant: "destructive" });
                  return;
                }
                window.open(`/approve/${first.publicToken}`, "_blank", "noopener,noreferrer");
              }}
            >
              <Eye className="h-4 w-4" /> {t("ticketDetail.openApprovalPage")}
            </Button>
            <Button
              variant="outline"
              className="gap-1.5"
              disabled={sendSectionDisabled || sendingReminderNow}
              onClick={async () => {
                const first = latestApprovalRequest;
                if (!first) {
                  toast({ title: t("ticketDetail.toasts.noApprovalRequestYet"), variant: "destructive" });
                  return;
                }
                if (!requireSmsActionConsent()) return;
                setSendingReminderNow(true);
                try {
                  await apiRequest(`/approvals/${first.id}/reminder-now`, {
                    method: "POST",
                    body: latestApprovalUsesSms
                      ? { smsConsentConfirmed: smsActionConsentConfirmed }
                      : undefined,
                  });
                  window.dispatchEvent(new Event("workscope:notifications-refresh"));
                  toast({ title: t("ticketDetail.toasts.reminderQueued") });
                  const nowIso = new Date().toISOString();
                  setTicket((prev) =>
                    prev
                      ? {
                          ...prev,
                          approvalEvents: [
                            ...prev.approvalEvents,
                            {
                              id: `temp-reminder-${Date.now()}`,
                              eventType: "REMINDER_SENT",
                              actorName: "system",
                              actorEmail: "system",
                              happenedAt: nowIso,
                            },
                          ],
                        }
                      : prev,
                  );
                  void loadTicket();
                } catch (e) {
                  toast({ title: t("ticketDetail.toasts.reminderFailedTitle"), description: e instanceof Error ? e.message : t("common.requestFailed"), variant: "destructive" });
                } finally {
                  setSendingReminderNow(false);
                }
              }}
            >
              {sendingReminderNow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              {sendingReminderNow ? t("login.sending") : t("ticketDetail.sendReminderNow")}
            </Button>
          </div>
        ) : null}
      </div>

      {isPendingApprovalTicket ? smsFailureWarning : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-5 space-y-4">
          <div>
            <p className="text-sm text-[#0C1F4F]">{t("ticketDetail.totalValue")}</p>
            <p className="text-2xl font-bold text-accent">{formatCurrency(ticket.totalAmount)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <p><span className="text-muted-foreground">{t("ticketDetail.project")}:</span> {ticket.project.name}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.client")}:</span> {ticket.client?.name ?? "-"}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.clientEmail")}:</span> {resolvedClientEmail}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.clientAddress")}:</span> {resolvedClientAddress}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.createdBy")}:</span> {ticket.createdBy.fullName}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.createdAt")}:</span> {formatDateTime(ticket.createdAt)}</p>
            <p><span className="text-muted-foreground">{t("ticketDetail.approvedAt")}:</span> {formatDateTime(ticket.approvedAt)}</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-5 space-y-3">
          <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.description")}</h2>
          <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.description || t("ticketDetail.noDescription")}</p>
        </div>
      </div>

      {changeOrderDetails ? (
        <div className="rounded-lg border bg-card p-5">
          <h2 className="font-semibold text-[#0C1F4F]">Change Order Details</h2>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {changeOrderDetails.reason ? (
              <p>
                <span className="text-muted-foreground">Reason for change:</span>{" "}
                <span className="font-medium text-[#0C1F4F]">{changeOrderDetails.reason}</span>
              </p>
            ) : null}
            {changeOrderDetails.changeType ? (
              <p>
                <span className="text-muted-foreground">Change type:</span>{" "}
                <span className="font-medium text-[#0C1F4F]">{changeOrderDetails.changeType}</span>
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {extraWorkDetails ? (
        <div className="rounded-lg border bg-card p-5">
          <h2 className="font-semibold text-[#0C1F4F]">Extra Work Details</h2>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Extra work reason:</span>{" "}
              <span className="font-medium text-[#0C1F4F]">{extraWorkDetails.reason}</span>
            </p>
          </div>
        </div>
      ) : null}

      {hasInternalEstimate ? (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="border-b px-5 py-4">
              <h2 className="font-semibold text-[#0C1F4F]">Internal Estimate</h2>
            </div>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${hasInternalEstimateImages ? "min-w-[820px]" : "min-w-[760px]"}`}>
                <thead>
                  <tr className="border-b bg-muted/50">
                    {hasInternalEstimateImages ? (
                      <th className="px-4 py-3 text-left text-[#0C1F4F]">Image</th>
                    ) : null}
                    <th className="px-4 py-3 text-left text-[#0C1F4F]">Description</th>
                    <th className="px-4 py-3 text-left text-[#0C1F4F]">Category</th>
                    <th className="px-4 py-3 text-right text-[#0C1F4F]">Qty</th>
                    <th className="px-4 py-3 text-left text-[#0C1F4F]">Unit</th>
                    <th className="px-4 py-3 text-right text-[#0C1F4F]">Rate</th>
                    <th className="px-4 py-3 text-right text-[#0C1F4F]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {internalEstimateRows.map((row, index) => {
                    const rowKey = row.id ?? `${row.description}-${index}`;
                    return (
                      <Fragment key={rowKey}>
                        <tr className="border-b align-top">
                          {hasInternalEstimateImages ? (
                            <td className="px-4 py-3">
                              {row.thumbnailUrl ? (
                                <img src={row.thumbnailUrl} alt="" className="h-10 w-10 rounded border object-cover" />
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                          ) : null}
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#0C1F4F]">{row.description}</p>
                          </td>
                          <td className="px-4 py-3">{formatPricingCategoryLabel(row.category)}</td>
                          <td className="px-4 py-3 text-right">{formatCompactNumber(Number(row.quantity || 0))}</td>
                          <td className="px-4 py-3">{formatPricingUnitLabel(row.unit)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(row.unitCost ?? 0)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(row.lineTotal ?? 0)}</td>
                        </tr>
                        {row.internalNote ? (
                          <tr className="border-b bg-muted/20">
                            <td
                              colSpan={hasInternalEstimateImages ? 7 : 6}
                              className="px-4 pb-3 pt-0 text-xs text-muted-foreground"
                            >
                              <span className="font-semibold text-[#51698F]">Internal note:</span>{" "}
                              {row.internalNote}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t bg-muted/20 px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[#0C1F4F]">Estimate summary</h3>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    {internalEstimateSummary.categoryTotals.map(({ label, amount }) => (
                      <span key={label} className="text-muted-foreground">
                        {label}{" "}
                        <span className="font-medium text-foreground">{formatCurrency(amount)}</span>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-md border bg-background px-4 py-2 text-sm lg:text-right">
                  <p className="text-muted-foreground">Internal estimate total</p>
                  <p className="text-lg font-bold text-[#0C1F4F]">{formatCurrency(internalEstimateSummary.total)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-5">
            <h2 className="font-semibold text-[#0C1F4F]">Customer-facing summary</h2>
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-muted-foreground">Display mode</p>
                <p className="font-semibold text-[#0C1F4F]">{formatCustomerDisplayMode(pricingMetadata?.customerDisplayMode)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Customer price</p>
                <p className="font-semibold text-[#0C1F4F]">{formatCurrency(pricingMetadata?.customerPrice ?? ticket.subtotalAmount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Discount / Tax</p>
                <p className="font-semibold text-[#0C1F4F]">
                  -{formatCurrency(pricingMetadata?.discountAmount ?? 0)} / {formatCurrency(pricingMetadata?.taxAmount ?? ticket.taxAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Total sent for approval</p>
                <p className="font-bold text-accent">{formatCurrency(pricingMetadata?.finalAmount ?? ticket.totalAmount)}</p>
              </div>
            </div>
          </div>
        </div>
      ) : hasNewPricingMetadata ? (
        <div className="rounded-lg border bg-card p-5">
          <h2 className="font-semibold text-[#0C1F4F]">Pricing</h2>
          <p className="mt-1 text-sm text-muted-foreground">This ticket was sent as a final price only.</p>
          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Customer price</p>
              <p className="font-semibold text-[#0C1F4F]">{formatCurrency(pricingMetadata?.customerPrice ?? ticket.subtotalAmount)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Discount</p>
              <p className="font-semibold text-[#0C1F4F]">-{formatCurrency(pricingMetadata?.discountAmount ?? 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Tax</p>
              <p className="font-semibold text-[#0C1F4F]">{formatCurrency(pricingMetadata?.taxAmount ?? ticket.taxAmount)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Total sent for approval</p>
              <p className="font-bold text-accent">{formatCurrency(pricingMetadata?.finalAmount ?? ticket.totalAmount)}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.costBreakdown")}</h2>
            {hasCostBreakdown ? (
              <span className="text-sm text-muted-foreground">
                {t("ticketDetail.subtotal")} {formatCurrency(ticket.subtotalAmount)} | {t("ticketDetail.tax")} {formatCurrency(ticket.taxAmount)}
              </span>
            ) : null}
          </div>
          {hasCostBreakdown ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-[#0C1F4F]">{t("ticketDetail.table.category")}</th>
                  <th className="px-4 py-3 text-left text-[#0C1F4F]">{t("ticketDetail.table.description")}</th>
                  <th className="px-4 py-3 text-right text-[#0C1F4F]">{t("ticketDetail.table.qty")}</th>
                  {hasDisplayUnitColumn ? (
                    <th className="px-4 py-3 text-left text-[#0C1F4F]">{t("ticketDetail.table.unit")}</th>
                  ) : null}
                  <th className="px-4 py-3 text-right text-[#0C1F4F]">{t("ticketDetail.table.unitCost")}</th>
                  <th className="px-4 py-3 text-right text-[#0C1F4F]">{t("ticketDetail.table.total")}</th>
                </tr>
              </thead>
              <tbody>
                {costBreakdownRows.map(({ item, qtyText, unitText }) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="px-4 py-3">{lineItemCategoryText(item.category)}</td>
                    <td className="px-4 py-3">{item.description}</td>
                    <td className="px-4 py-3 text-right">{qtyText}</td>
                    {hasDisplayUnitColumn ? <td className="px-4 py-3">{unitText}</td> : null}
                    <td className="px-4 py-3 text-right">{formatCurrency(item.unitCost)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-5 py-4 text-sm text-muted-foreground">
              No costs were defined for this ticket.
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.attachments")}</h2>
          <label
            className={`inline-flex items-center px-3 py-2 rounded-md border text-sm ${
              uploadingAttachment ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40"
            }`}
          >
            {uploadingAttachment ? t("ticketDetail.uploading") : t("ticketDetail.addFile")}
            <input
              type="file"
              className="hidden"
              disabled={uploadingAttachment}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                e.currentTarget.value = "";
                void handleUploadAttachment(file);
              }}
            />
          </label>
        </div>
        {ticket.attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ticketDetail.noAttachments")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {ticket.attachments.map((file) => (
              <div key={file.id} className="rounded-md border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{file.fileName}</p>
                    <p className="text-xs text-muted-foreground">{file.tag}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={deletingAttachmentId === file.id}
                    onClick={() => void handleDeleteAttachment(file.id)}
                  >
                    {deletingAttachmentId === file.id ? t("ticketDetail.removing") : t("team.remove")}
                  </Button>
                </div>

                {isImageAttachment(file.mimeType, file.fileName) && file.url ? (
                  <a href={file.url} target="_blank" rel="noreferrer">
                    <img
                      src={file.url}
                      alt={file.fileName}
                      className="h-20 w-28 object-cover rounded border"
                    />
                  </a>
                ) : file.url ? (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-accent hover:underline"
                  >
                    {t("ticketDetail.viewFile")}
                  </a>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("ticketDetail.fileUrlUnavailable")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showTicketPdfSection ? (
        <div className="rounded-lg border bg-card p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.ticketPdf")}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={!canSendTicketPdfEmail || sendingTicketPdfEmail}
                onClick={() => void sendTicketPdfEmail()}
              >
                {sendingTicketPdfEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sendingTicketPdfEmail ? t("login.sending") : t("ticketDetail.sendTicketPdfToClient")}
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={downloadingTicketPdf}
                onClick={() => void downloadTicketPdf()}
              >
                {downloadingTicketPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloadingTicketPdf ? t("common.preparing") : t("ticketDetail.downloadTicketPdf")}
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("ticketDetail.availableForApprovedOnly")}
          </p>
          {!canSendTicketPdfEmail ? (
            <p className="text-xs text-muted-foreground">
              {t("ticketDetail.clientEmailRequiredForPdfSend")}
            </p>
          ) : null}
          <div className="rounded-md border bg-muted/20 overflow-hidden">
            {loadingTicketPdfMeta ? (
              <div className="h-[420px] flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("ticketDetail.loadingPdfPreview")}
              </div>
            ) : ticketPdfUrl ? (
              <iframe
                title={t("ticketDetail.ticketPdfPreview")}
                src={`${ticketPdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full h-[420px] bg-white"
              />
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground px-4 text-center">
                {t("ticketDetail.pdfPreviewCurrentlyUnavailable")}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {canUseManualOverride ? (
        <div className="rounded-lg border bg-card p-5 space-y-3">
          <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.adminActions")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("ticketDetail.finalizeManuallyDescription")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => openManualDecision("APPROVED")}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {t("ticketDetail.markAsApprovedAdmin")}
            </Button>
            <Button variant="destructive" onClick={() => openManualDecision("REJECTED")}>
              {t("ticketDetail.markAsRejectedAdmin")}
            </Button>
          </div>
        </div>
      ) : null}

      {showSendForApprovalSection && !isPendingApprovalTicket ? (
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.sendForApproval")}</h2>
        {sendSectionDisabled ? (
          <div className="rounded-md border border-muted-foreground/20 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {t("ticketDetail.finalizedByAdminNoDeliveryNeeded")}
          </div>
        ) : null}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("ticketDetail.deliveryMethod")}</Label>
            {canUseSmsDelivery ? (
              <select
                value={deliveryMethod}
                onChange={(e) => setDeliveryMethod(e.target.value as "EMAIL" | "SMS" | "EMAIL_AND_SMS")}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="EMAIL">{t("ticketDetail.deliveryEmail")}</option>
                <option value="SMS">{t("ticketDetail.deliverySms")}</option>
                <option value="EMAIL_AND_SMS">{t("ticketDetail.deliveryEmailAndSms")}</option>
              </select>
            ) : (
              <div className="h-9 w-full rounded-md border bg-muted/30 px-3 text-sm flex items-center text-foreground">
                {t("ticketDetail.deliveryEmail")}
              </div>
            )}
            {!canUseSmsDelivery ? (
              <p className="text-xs text-muted-foreground">{smsAvailabilityReason}</p>
            ) : null}
          </div>
          {workspaceAutoRemindersEnabled ? (
            <div className="space-y-1.5">
              <Label>{t("ticketDetail.autoReminder")}</Label>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={reminderCadenceOption}
                onChange={(e) => setReminderCadenceOption(e.target.value)}
              >
                <option value="1">{t("ticketDetail.dayCount", { count: 1 })}</option>
                <option value="2">{t("ticketDetail.dayCount", { count: 2 })}</option>
                <option value="3">{t("ticketDetail.recommendedDayCount", { count: 3 })}</option>
                <option value="5">{t("ticketDetail.dayCount", { count: 5 })}</option>
                <option value="7">{t("ticketDetail.dayCount", { count: 7 })}</option>
                <option value="10">{t("ticketDetail.dayCount", { count: 10 })}</option>
                <option value="14">{t("ticketDetail.dayCount", { count: 14 })}</option>
                <option value="21">{t("ticketDetail.dayCount", { count: 21 })}</option>
                <option value="30">{t("ticketDetail.dayCount", { count: 30 })}</option>
                <option value="custom">{t("ticketDetail.custom")}</option>
              </select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>{t("ticketDetail.autoReminder")}</Label>
              <div className="h-9 w-full rounded-md border bg-muted/30 px-3 text-sm flex items-center text-muted-foreground">
                {t("createTicket.autoRemindersOff")}
              </div>
            </div>
          )}
          <Input placeholder={t("ticketDetail.recipientName")} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
          {deliveryMethod === "EMAIL" || deliveryMethod === "EMAIL_AND_SMS" ? (
            <Input placeholder={t("ticketDetail.recipientEmail")} type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
          ) : null}
          {deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS" ? (
            <div className="space-y-1.5">
              <Input
                placeholder={t("ticketDetail.recipientPhone")}
                type="tel"
                value={recipientPhone}
                onChange={(e) => {
                  setRecipientPhone(e.target.value);
                  if (recipientPhoneError) setRecipientPhoneError("");
                }}
                className={recipientPhoneError ? "border-destructive focus-visible:ring-destructive/40" : ""}
              />
              {recipientPhoneError ? <p className="text-xs text-destructive">{recipientPhoneError}</p> : null}
            </div>
          ) : null}
          {deliveryUsesSms ? (
            <SmsConsentConfirmation
              checked={smsConsentConfirmed}
              onCheckedChange={(checked) => {
                setSmsConsentConfirmed(checked);
                if (checked) setSmsConsentError("");
              }}
              error={smsConsentError}
              className="sm:col-span-2"
            />
          ) : null}
          {workspaceAutoRemindersEnabled && reminderCadenceOption === "custom" ? (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("ticketDetail.customCadenceDays")}</Label>
              <Input
                type="number"
                min={1}
                value={customReminderCadenceDays}
                onChange={(e) => setCustomReminderCadenceDays(e.target.value)}
                placeholder={t("ticketDetail.customCadencePlaceholder")}
              />
            </div>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button onClick={sendApproval} disabled={sendSectionDisabled || !canSend || sending} className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5">
            <Send className="h-4 w-4" /> {sending ? t("login.sending") : deliveryMethod === "EMAIL_AND_SMS" ? t("ticketDetail.sendApprovalEmailAndSms") : deliveryMethod === "SMS" ? t("ticketDetail.sendApprovalSms") : t("ticketDetail.sendApproval")}
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={sendSectionDisabled}
            onClick={() => {
              const first = latestApprovalRequest;
              if (!first?.publicToken) {
                toast({ title: t("ticketDetail.toasts.noApprovalLinkYet"), variant: "destructive" });
                return;
              }
              window.open(`/approve/${first.publicToken}`, "_blank", "noopener,noreferrer");
            }}
          >
            <Eye className="h-4 w-4" /> {t("ticketDetail.openApprovalPage")}
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={sendSectionDisabled || sendingReminderNow}
            onClick={async () => {
              const first = latestApprovalRequest;
              if (!first) {
                toast({ title: t("ticketDetail.toasts.noApprovalRequestYet"), variant: "destructive" });
                return;
              }
              if (!requireSmsActionConsent()) return;
              setSendingReminderNow(true);
              try {
                await apiRequest(`/approvals/${first.id}/reminder-now`, {
                  method: "POST",
                  body: latestApprovalUsesSms
                    ? { smsConsentConfirmed: smsActionConsentConfirmed }
                    : undefined,
                });
                window.dispatchEvent(new Event("workscope:notifications-refresh"));
                toast({ title: t("ticketDetail.toasts.reminderQueued") });
                const nowIso = new Date().toISOString();
                setTicket((prev) =>
                  prev
                    ? {
                        ...prev,
                        approvalEvents: [
                          ...prev.approvalEvents,
                          {
                            id: `temp-reminder-${Date.now()}`,
                            eventType: "REMINDER_SENT",
                            actorName: "system",
                            actorEmail: "system",
                            happenedAt: nowIso,
                          },
                        ],
                      }
                    : prev,
                );
                void loadTicket();
              } catch (e) {
                toast({ title: t("ticketDetail.toasts.reminderFailedTitle"), description: e instanceof Error ? e.message : t("common.requestFailed"), variant: "destructive" });
              } finally {
                setSendingReminderNow(false);
              }
            }}
          >
            {sendingReminderNow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
            {sendingReminderNow ? t("login.sending") : t("ticketDetail.sendReminderNow")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("ticketDetail.lastReminder")}: {lastReminderAt ? new Date(lastReminderAt).toLocaleString() : "—"}
        </p>
        {latestApprovalSmsMessages.length > 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-[#0C1F4F]">{t("ticketDetail.smsDeliveryStatus")}</p>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${smsStatusClassName(latestApprovalSmsMessages[0].latestStatus)}`}>
                {smsStatusLabel(latestApprovalSmsMessages[0].latestStatus)}
              </span>
            </div>
            <div className="space-y-2">
              {latestApprovalSmsMessages.slice(0, 3).map((message) => (
                <div key={message.id} className="rounded-md border bg-white px-3 py-2 text-xs text-slate-600">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-[#0C1F4F]">{smsMessageKindLabel(message.messageKind)}</span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${smsStatusClassName(message.latestStatus)}`}>
                      {smsStatusLabel(message.latestStatus)}
                    </span>
                  </div>
                  <p className="mt-1">{message.recipientPhone}</p>
                  <p className="mt-1">
                    {t("ticketDetail.lastStatusUpdate")}: {formatDateTime(message.lastStatusAt ?? message.createdAt)}
                  </p>
                  {message.errorMessage ? (
                    <p className="mt-1 text-destructive">{message.errorMessage}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      ) : null}

      {(ticket.status === "NEEDS_CLARIFICATION" || clarificationThread.length > 0) ? (
        <div className="rounded-lg border bg-card p-5 space-y-4">
          <h2 className="font-semibold text-[#0C1F4F]">{isApprovedTicket ? t("ticketDetail.approvalDetails") : t("ticketDetail.clarification")}</h2>

          {latestCustomerClarification ? (
            <div className="rounded-md border border-warning/40 bg-warning/10 p-4 space-y-2">
              <p className="text-sm">
                <span className="text-muted-foreground">{t("ticketDetail.requestedBy")}:</span>{" "}
                {latestApprovalRequest?.recipientName || latestApprovalRequest?.recipientEmail || t("ticketDetail.customer")}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">{t("ticketDetail.requestedAt")}:</span>{" "}
                {new Date(latestCustomerClarification.happenedAt).toLocaleString()}
              </p>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t("ticketDetail.message")}:</p>
                <p className="text-sm whitespace-pre-wrap">{latestCustomerClarification.message}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("ticketDetail.noCustomerClarificationMessage")}</p>
          )}

          {clarificationThreadAfterSummary.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-[#0C1F4F]">{t("ticketDetail.thread")}</h3>
              {clarificationThreadAfterSummary.map((item) => (
                <div key={item.id} className="rounded-md border p-3 space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {item.authorLabel} · {new Date(item.happenedAt).toLocaleString()}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{item.message}</p>
                </div>
              ))}
            </div>
          ) : null}

          {(user?.role === "ADMIN" || user?.role === "PM") ? (
            <div className="space-y-3 pt-1">
              <Label htmlFor="clarification-response">{t("ticketDetail.yourResponse")}</Label>
              <textarea
                id="clarification-response"
                className="min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder={isApprovedTicket ? t("ticketDetail.approvedTicketResponsePlaceholder") : t("ticketDetail.clarificationResponsePlaceholder")}
                value={clarificationMessage}
                onChange={(e) => setClarificationMessage(e.target.value)}
              />
              <div>
                <Button
                  onClick={sendClarificationResponse}
                  disabled={sendSectionDisabled || !canRespondClarification || respondingClarification}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {respondingClarification ? t("login.sending") : t("ticketDetail.sendResponse")}
                </Button>
              </div>
            </div>
          ) : null}

          {!isApprovedTicket && ticket.status !== "NEEDS_CLARIFICATION" ? (
          <div className="pt-2 border-t space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("ticketDetail.approvalDeliveryActions")}</p>
            {sendSectionDisabled ? (
              <div className="rounded-md border border-muted-foreground/20 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t("ticketDetail.finalizedByAdminActionsDisabled")}
              </div>
            ) : null}
            {smsFailureWarning}
            {latestApprovalUsesSms ? (
              <SmsConsentConfirmation
                checked={smsActionConsentConfirmed}
                onCheckedChange={(checked) => {
                  setSmsActionConsentConfirmed(checked);
                  if (checked) setSmsActionConsentError("");
                }}
                error={smsActionConsentError}
              />
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={sendSectionDisabled}
                onClick={async () => {
                  const first = latestApprovalRequest;
                  if (!first) {
                    toast({ title: t("ticketDetail.toasts.noApprovalRequestYet"), variant: "destructive" });
                    return;
                  }
                  if (!requireSmsActionConsent()) return;
                  try {
                    await apiRequest(`/approvals/${first.id}/resend-link`, {
                      method: "POST",
                      body: latestApprovalUsesSms
                        ? { smsConsentConfirmed: smsActionConsentConfirmed }
                        : undefined,
                    });
                    window.dispatchEvent(new Event("workscope:notifications-refresh"));
                    toast({ title: t("ticketDetail.toasts.approvalLinkResent") });
                    void loadTicket();
                  } catch (e) {
                    toast({
                      title: t("ticketDetail.toasts.resendFailedTitle"),
                      description: e instanceof Error ? e.message : t("common.tryAgain"),
                      variant: "destructive",
                    });
                  }
                }}
              >
                <Send className="h-4 w-4" /> {t("ticketDetail.resendApprovalLink")}
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                disabled={sendSectionDisabled}
                onClick={() => {
                  const first = latestApprovalRequest;
                  if (!first?.publicToken) {
                    toast({ title: t("ticketDetail.toasts.noApprovalLinkYet"), variant: "destructive" });
                    return;
                  }
                  window.open(`/approve/${first.publicToken}`, "_blank", "noopener,noreferrer");
                }}
              >
                <Eye className="h-4 w-4" /> {t("ticketDetail.openApprovalPage")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("ticketDetail.lastReminder")}: {lastReminderAt ? new Date(lastReminderAt).toLocaleString() : "—"}
            </p>
          </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold text-[#0C1F4F]">{t("ticketDetail.approvalTimeline")}</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setShowTechnicalEvents((prev) => !prev)}
          >
            {showTechnicalEvents ? t("ticketDetail.hideTechnicalEvents") : t("ticketDetail.showTechnicalEvents")}
          </Button>
        </div>
        {timelineEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ticketDetail.noEventsYet")}</p>
        ) : (
          <div className="space-y-2">
            {timelineEvents.map((event) => (
              <div key={event.id} className="text-sm border rounded-md p-3">
                <p className="font-medium">{approvalEventText(event.eventType)}</p>
                <p className="text-muted-foreground text-xs">
                  {new Date(event.happenedAt).toLocaleString()} · {event.actorName || event.actorEmail || t("ticketDetail.system").toLowerCase()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={manualDecisionOpen}
        onOpenChange={(open) => {
          if (!open && !processingManualDecision) {
            setManualDecisionOpen(false);
            setManualDecisionReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {manualDecisionType === "APPROVED"
                ? t("ticketDetail.markApprovedQuestion")
                : t("ticketDetail.markRejectedQuestion")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ticketDetail.finalizeWithAdminOverride", { ticketNumber: ticket.ticketNumber })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="manual-decision-reason">
              {manualDecisionType === "APPROVED"
                  ? t("ticketDetail.internalNoteOptional")
                  : t("ticketDetail.rejectionReason")}
            </Label>
            <textarea
              id="manual-decision-reason"
              className="min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={manualDecisionReason}
              onChange={(e) => setManualDecisionReason(e.target.value)}
              placeholder={
                manualDecisionType === "APPROVED"
                  ? t("ticketDetail.optionalNoteForTimeline")
                  : t("ticketDetail.explainRejection")
              }
            />
            {manualDecisionType === "REJECTED" && requireRejectionComment ? (
              <p className="text-xs text-muted-foreground">
                {t("ticketDetail.rejectionReasonRequiredByWorkspace")}
              </p>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingManualDecision}>{t("createAccount.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void submitManualDecision();
              }}
              disabled={processingManualDecision}
              className={
                manualDecisionType === "REJECTED"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : undefined
              }
            >
              {processingManualDecision
                ? t("ticketDetail.updating")
                : manualDecisionType === "APPROVED"
                  ? t("ticketDetail.markApproved")
                  : t("ticketDetail.markRejected")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
